import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
  Plus, Search, Edit, Trash2, Eye, FileText, Calendar, Euro,
  Building2, User, ChevronDown, ChevronRight, Loader2, X,
  FileSpreadsheet, Download, Clock, CheckCircle, AlertCircle, ArrowLeft, History, Upload, RefreshCw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AccordiQuadroPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [accordi, setAccordi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAccordo, setEditingAccordo] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedAccordo, setSelectedAccordo] = useState(null);
  const [attivitaDialogOpen, setAttivitaDialogOpen] = useState(false);
  const [progettiCollegati, setProgettiCollegati] = useState([]);
  const [loadingProgetti, setLoadingProgetti] = useState(false);
  const [preventivoData, setPreventivoData] = useState(null); // Dati preventivo da convertire
  
  // Data
  const [clienti, setClienti] = useState([]);
  const [utenti, setUtenti] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    cliente_id: '',
    nome_accordo: '',
    descrizione: '',
    codice_accordo_quadro: '',
    pm_main_solution_id: '',
    referente_cliente: '',
    pm_cliente: '',
    tipo_conteggio: 'euro',
    castelletto: '',
    data_stipula: '',
    data_inizio_validita: '',
    data_fine_validita: '',
    preventivo_id: '', // Per tracciare la conversione
    stato: 'attivo'
  });
  
  // Stato per gestione listino
  const [listinoDialogOpen, setListinoDialogOpen] = useState(false);
  const [listino, setListino] = useState([]);
  const [loadingListino, setLoadingListino] = useState(false);
  const [nuovaVoceListino, setNuovaVoceListino] = useState({ 
    descrizione: '', 
    prezzo: '', 
    unita_misura: 'euro',  // 'euro', 'euro_ora', 'ore'
    ore: '' 
  });
  const listinoFileRef = React.useRef(null);
  
  // Form attività
  const [formAttivita, setFormAttivita] = useState({
    descrizione: '',
    importo_unitario: '',
    quantita: 1,
    unita_misura: 'ore',
    mese_riferimento: '',
    note: '',
    data_inizio: '',
    data_fine: '',
    tipo_impianto: '',
    varianti: ''
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  useEffect(() => {
    fetchAccordi();
    fetchClienti();
    fetchUtenti();
  }, []);

  // Gestisce arrivo da preventivo da convertire
  useEffect(() => {
    if (location.state?.fromPreventivo && location.state?.preventivoData) {
      const prevData = location.state.preventivoData;
      setPreventivoData(prevData);
      
      // Pre-compila il form con i dati del preventivo
      setFormData(prev => ({
        ...prev,
        cliente_id: prevData.cliente_id || '',
        nome_accordo: prevData.descrizione || `Accordo da ${prevData.numero_preventivo}`,
        descrizione: prevData.note || prevData.descrizione || '',
        castelletto: prevData.importo || '',
        preventivo_id: prevData.preventivo_id
      }));
      
      // Apri automaticamente il dialog
      setDialogOpen(true);
      toast.info(`Compila i dati per convertire il preventivo ${prevData.numero_preventivo} in Accordo Quadro`);
      
      // Pulisci lo state per evitare re-apertura al refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const fetchAccordi = async () => {
    try {
      const response = await axios.get(`${API}/api/accordi-quadro`, getAuthHeaders());
      setAccordi(response.data);
      
      // 🚨 Alert per AQ con budget critico (< 20%)
      const criticalAQ = response.data.filter(a => a.alert_level === 'critical');
      const warningAQ = response.data.filter(a => a.alert_level === 'warning');
      
      if (criticalAQ.length > 0) {
        toast.error(
          `⚠️ ATTENZIONE: ${criticalAQ.length} Accordo/i Quadro con castelletto sotto il 10%!`,
          { duration: 8000 }
        );
      } else if (warningAQ.length > 0) {
        toast.warning(
          `${warningAQ.length} Accordo/i Quadro con castelletto sotto il 20%`,
          { duration: 5000 }
        );
      }
    } catch (error) {
      console.error('Errore caricamento accordi:', error);
      toast.error('Errore nel caricamento degli accordi quadro');
    } finally {
      setLoading(false);
    }
  };

  const fetchClienti = async () => {
    try {
      const response = await axios.get(`${API}/api/clienti`, getAuthHeaders());
      setClienti(response.data);
    } catch (error) {
      console.error('Errore caricamento clienti:', error);
    }
  };

  const fetchUtenti = async () => {
    try {
      const response = await axios.get(`${API}/api/utenti`, getAuthHeaders());
      setUtenti(response.data.filter(u => u.ruolo === 'admin' || u.ambito === 'ingegnere'));
    } catch (error) {
      console.error('Errore caricamento utenti:', error);
    }
  };

  const fetchProgettiCollegati = async (accordoId) => {
    setLoadingProgetti(true);
    try {
      const response = await axios.get(`${API}/api/accordi-quadro/${accordoId}/progetti`, getAuthHeaders());
      setProgettiCollegati(response.data.progetti || []);
    } catch (error) {
      console.error('Errore caricamento progetti:', error);
      setProgettiCollegati([]);
    } finally {
      setLoadingProgetti(false);
    }
  };

  const resetForm = () => {
    setFormData({
      cliente_id: '',
      nome_accordo: '',
      descrizione: '',
      codice_accordo_quadro: '',
      pm_main_solution_id: '',
      referente_cliente: '',
      pm_cliente: '',
      tipo_conteggio: 'euro',
      castelletto: '',
      data_stipula: '',
      data_inizio_validita: '',
      data_fine_validita: '',
      stato: 'attivo'
    });
    setEditingAccordo(null);
  };

  // --- Funzioni Listino Attività ---
  const fetchListino = async (accordoId) => {
    setLoadingListino(true);
    try {
      const response = await axios.get(`${API}/api/accordi-quadro/${accordoId}/listino`, getAuthHeaders());
      setListino(response.data.listino || []);
    } catch (error) {
      console.error('Errore caricamento listino:', error);
      setListino([]);
    } finally {
      setLoadingListino(false);
    }
  };

  const handleDownloadTemplate = async (accordoId) => {
    try {
      const response = await axios.get(
        `${API}/api/accordi-quadro/${accordoId}/listino/template`,
        { ...getAuthHeaders(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `template_listino_${selectedAccordo?.codice_accordo_quadro || 'AQ'}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template scaricato!');
    } catch (error) {
      console.error('Errore download template:', error);
      toast.error('Errore nel download del template');
    }
  };

  const handleImportListino = async (e) => {
    if (!e.target.files?.length || !selectedAccordo) return;
    
    const file = e.target.files[0];
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const response = await axios.post(
        `${API}/api/accordi-quadro/${selectedAccordo.id}/listino/import`,
        formDataUpload,
        { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success(response.data.message);
      fetchListino(selectedAccordo.id);
      fetchAccordi(); // Ricarica per aggiornare i dati
    } catch (error) {
      console.error('Errore import listino:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'importazione del listino');
    } finally {
      if (listinoFileRef.current) listinoFileRef.current.value = '';
    }
  };

  const handleAddVoceListino = async () => {
    if (!nuovaVoceListino.descrizione.trim()) {
      toast.error('Inserisci la descrizione dell\'attività');
      return;
    }
    
    try {
      await axios.post(
        `${API}/api/accordi-quadro/${selectedAccordo.id}/listino/voce`,
        null,
        { 
          ...getAuthHeaders(), 
          params: { 
            descrizione: nuovaVoceListino.descrizione, 
            prezzo: parseFloat(nuovaVoceListino.prezzo) || 0,
            unita_misura: nuovaVoceListino.unita_misura || 'euro',
            ore: parseFloat(nuovaVoceListino.ore) || 0
          } 
        }
      );
      toast.success('Voce aggiunta al listino');
      setNuovaVoceListino({ descrizione: '', prezzo: '', unita_misura: 'euro', ore: '' });
      fetchListino(selectedAccordo.id);
    } catch (error) {
      console.error('Errore aggiunta voce:', error);
      toast.error('Errore nell\'aggiunta della voce');
    }
  };

  const handleDeleteVoceListino = async (voceId) => {
    if (!window.confirm('Eliminare questa voce dal listino?')) return;
    
    try {
      await axios.delete(
        `${API}/api/accordi-quadro/${selectedAccordo.id}/listino/voce/${voceId}`,
        getAuthHeaders()
      );
      toast.success('Voce eliminata');
      fetchListino(selectedAccordo.id);
    } catch (error) {
      console.error('Errore eliminazione voce:', error);
      toast.error('Errore nell\'eliminazione della voce');
    }
  };

  const openListinoDialog = (accordo) => {
    setSelectedAccordo(accordo);
    fetchListino(accordo.id);
    setListinoDialogOpen(true);
  };
  // --- Fine Funzioni Listino ---

  const handleSubmit = async () => {
    if (!formData.cliente_id || !formData.nome_accordo) {
      toast.error('Compila i campi obbligatori (Cliente e Nome Accordo)');
      return;
    }

    try {
      const submitData = {
        ...formData,
        castelletto: parseFloat(formData.castelletto) || 0
      };

      if (editingAccordo) {
        await axios.put(`${API}/api/accordi-quadro/${editingAccordo.id}`, submitData, getAuthHeaders());
        toast.success('Accordo Quadro aggiornato con successo!');
      } else {
        await axios.post(`${API}/api/accordi-quadro`, submitData, getAuthHeaders());
        toast.success('Accordo Quadro creato con successo!');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchAccordi();
    } catch (error) {
      console.error('Errore salvataggio:', error);
      toast.error('Errore nel salvataggio dell\'accordo quadro');
    }
  };

  const handleEdit = (accordo) => {
    setEditingAccordo(accordo);
    setFormData({
      cliente_id: accordo.cliente_id || '',
      nome_accordo: accordo.nome_accordo || '',
      descrizione: accordo.descrizione || '',
      codice_accordo_quadro: accordo.codice_accordo_quadro || '',
      pm_main_solution_id: accordo.pm_main_solution_id || '',
      referente_cliente: accordo.referente_cliente || '',
      pm_cliente: accordo.pm_cliente || '',
      tipo_conteggio: accordo.tipo_conteggio || 'euro',
      castelletto: accordo.castelletto?.toString() || '',
      data_stipula: accordo.data_stipula || '',
      data_inizio_validita: accordo.data_inizio_validita || '',
      data_fine_validita: accordo.data_fine_validita || '',
      stato: accordo.stato || 'attivo'
    });
    setDialogOpen(true);
  };

  const handleDelete = async (accordoId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo accordo quadro?')) return;
    
    try {
      await axios.delete(`${API}/api/accordi-quadro/${accordoId}`, getAuthHeaders());
      toast.success('Accordo Quadro eliminato con successo!');
      fetchAccordi();
    } catch (error) {
      console.error('Errore eliminazione:', error);
      toast.error('Errore nell\'eliminazione dell\'accordo quadro');
    }
  };

  const handleViewDetails = async (accordo) => {
    setSelectedAccordo(accordo);
    setDetailDialogOpen(true);
    fetchProgettiCollegati(accordo.id);
  };

  const handleAddAttivita = async () => {
    if (!formAttivita.descrizione) {
      toast.error('Inserisci una descrizione per l\'attività');
      return;
    }

    try {
      // Recupera codice cliente dall'accordo selezionato
      const cliente = clienti.find(c => c.id === selectedAccordo.cliente_id);
      const codiceCliente = cliente?.codice_cliente || '';
      
      await axios.post(
        `${API}/api/accordi-quadro/${selectedAccordo.id}/attivita`,
        {
          ...formAttivita,
          importo_unitario: parseFloat(formAttivita.importo_unitario) || 0,
          quantita: parseFloat(formAttivita.quantita) || 1,
          codice_cliente: codiceCliente
        },
        getAuthHeaders()
      );
      
      toast.success('Attività aggiunta con successo!');
      setAttivitaDialogOpen(false);
      setFormAttivita({
        descrizione: '',
        importo_unitario: '',
        quantita: 1,
        unita_misura: 'ore',
        mese_riferimento: '',
        note: '',
        data_inizio: '',
        data_fine: '',
        tipo_impianto: '',
        varianti: ''
      });
      
      // Ricarica accordo
      const response = await axios.get(`${API}/api/accordi-quadro/${selectedAccordo.id}`, getAuthHeaders());
      setSelectedAccordo(response.data);
      fetchAccordi();
    } catch (error) {
      console.error('Errore aggiunta attività:', error);
      toast.error('Errore nell\'aggiunta dell\'attività');
    }
  };

  const handleUpdateAttivitaStato = async (attivitaId, nuovoStato, extraData = {}) => {
    try {
      await axios.put(
        `${API}/api/accordi-quadro/${selectedAccordo.id}/attivita/${attivitaId}`,
        { stato_rendicontazione: nuovoStato, ...extraData },
        getAuthHeaders()
      );
      
      toast.success('Stato attività aggiornato!');
      
      // Ricarica accordo
      const response = await axios.get(`${API}/api/accordi-quadro/${selectedAccordo.id}`, getAuthHeaders());
      setSelectedAccordo(response.data);
      fetchAccordi();
    } catch (error) {
      console.error('Errore aggiornamento:', error);
      toast.error('Errore nell\'aggiornamento dello stato');
    }
  };

  const handleDeleteAttivita = async (attivitaId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa attività?')) return;
    
    try {
      await axios.delete(
        `${API}/api/accordi-quadro/${selectedAccordo.id}/attivita/${attivitaId}`,
        getAuthHeaders()
      );
      
      toast.success('Attività eliminata!');
      
      // Ricarica accordo
      const response = await axios.get(`${API}/api/accordi-quadro/${selectedAccordo.id}`, getAuthHeaders());
      setSelectedAccordo(response.data);
      fetchAccordi();
    } catch (error) {
      console.error('Errore eliminazione:', error);
      toast.error('Errore nell\'eliminazione dell\'attività');
    }
  };

  const handleRicalcolaCastelletto = async (accordo) => {
    try {
      await axios.post(
        `${API}/api/accordi-quadro/${accordo.id}/ricalcola-castelletto`,
        {},
        getAuthHeaders()
      );
      
      toast.success('Castelletto ricalcolato!');
      fetchAccordi();
    } catch (error) {
      console.error('Errore ricalcolo castelletto:', error);
      toast.error('Errore nel ricalcolo del castelletto');
    }
  };
  
  // Export attività in Excel
  const handleExportAttivitaExcel = async (meseFilter = null, statoFilter = null) => {
    try {
      let url = `${API}/api/accordi-quadro/${selectedAccordo.id}/export-attivita-excel`;
      const params = new URLSearchParams();
      if (meseFilter) params.append('mese', meseFilter);
      if (statoFilter) params.append('stato', statoFilter);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `attivita_${selectedAccordo.numero_accordo || 'AQ'}${meseFilter ? '_' + meseFilter : ''}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Export completato!');
    } catch (error) {
      console.error('Errore export:', error);
      toast.error('Errore nell\'export delle attività');
    }
  };
  
  // Export progetti collegati in Excel
  const handleExportProgettiExcel = async () => {
    try {
      const response = await axios.get(
        `${API}/api/accordi-quadro/${selectedAccordo.id}/export-progetti-excel`,
        {
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `progetti_${selectedAccordo.numero_accordo || 'AQ'}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('Export progetti completato!');
    } catch (error) {
      console.error('Errore export progetti:', error);
      toast.error('Errore nell\'export dei progetti');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };
  
  // Formatta castelletto in base al tipo_conteggio (€ o h)
  const formatCastelletto = (amount, tipoConteggio) => {
    if (tipoConteggio === 'ore') {
      return `${(amount || 0).toLocaleString('it-IT')} h`;
    }
    return formatCurrency(amount);
  };
  
  // Aggiorna stato fatturazione progetto
  const handleUpdateProgettoFatturazione = async (progettoId, field, value) => {
    try {
      await axios.put(
        `${API}/api/progetti/${progettoId}`,
        { [field]: value },
        getAuthHeaders()
      );
      
      // Aggiorna la lista progetti localmente
      setProgettiCollegati(prev => prev.map(p => 
        p.id === progettoId ? { ...p, [field]: value } : p
      ));
      
      toast.success(field === 'stato_fatturazione' ? 'Stato fatturazione aggiornato!' : 'Codice OdA aggiornato!');
    } catch (error) {
      console.error('Errore aggiornamento:', error);
      toast.error('Errore nell\'aggiornamento');
    }
  };
  
  // Aggiorna fatturazione per TUTTO il mese (tutti i progetti del mese)
  const handleUpdateMeseFatturazione = async (mese, progetti, field, value) => {
    try {
      // Aggiorna tutti i progetti del mese
      const promises = progetti.map(progetto => 
        axios.put(
          `${API}/api/progetti/${progetto.id}`,
          { [field]: value },
          getAuthHeaders()
        )
      );
      
      await Promise.all(promises);
      
      // Aggiorna la lista progetti localmente
      const progettiIds = progetti.map(p => p.id);
      setProgettiCollegati(prev => prev.map(p => 
        progettiIds.includes(p.id) ? { ...p, [field]: value } : p
      ));
      
      const meseLabel = mese === 'Non assegnato' ? mese : new Date(mese + '-01').toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
      toast.success(`${field === 'stato_fatturazione' ? 'Stato fatturazione' : 'Codice OdA'} aggiornato per ${meseLabel} (${progetti.length} progetti)`);
    } catch (error) {
      console.error('Errore aggiornamento mese:', error);
      toast.error('Errore nell\'aggiornamento');
    }
  };
  
  // Raggruppa progetti per mese (basato su data_fine_prevista)
  const getProgettiPerMese = () => {
    const grouped = {};
    progettiCollegati.forEach(progetto => {
      const dataFine = progetto.data_fine_prevista || progetto.data_fine_effettiva;
      let mese = 'Non assegnato';
      if (dataFine) {
        const date = new Date(dataFine);
        mese = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      }
      if (!grouped[mese]) grouped[mese] = [];
      grouped[mese].push(progetto);
    });
    // Ordina per mese decrescente
    return Object.entries(grouped).sort((a, b) => b[0].localeCompare(a[0]));
  };

  const getStatoBadge = (stato) => {
    const badges = {
      attivo: 'bg-green-100 text-green-700',
      in_scadenza: 'bg-yellow-100 text-yellow-700',
      scaduto: 'bg-red-100 text-red-700',
      chiuso: 'bg-slate-100 text-slate-700'
    };
    const labels = {
      attivo: 'Attivo',
      in_scadenza: 'In Scadenza',
      scaduto: 'Scaduto',
      chiuso: 'Chiuso'
    };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${badges[stato] || badges.attivo}`}>{labels[stato] || stato}</span>;
  };

  const filteredAccordi = accordi.filter(a => {
    const search = searchText.toLowerCase();
    return (
      a.numero_accordo?.toLowerCase().includes(search) ||
      a.nome_accordo?.toLowerCase().includes(search) ||
      a.cliente_nome?.toLowerCase().includes(search)
    );
  });

  const getCurrentMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-3 py-4 sm:px-6 sm:py-8">
          {/* Freccia torna alla dashboard */}
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-4 text-white/80 hover:text-white hover:bg-white/10 -ml-2"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Torna alla Dashboard
          </Button>
          
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-xl sm:text-3xl font-bold">Accordi Quadro</h1>
              <p className="text-indigo-100 mt-1">Gestione contratti quadro e rendicontazione mensile</p>
            </div>
            <Button
              onClick={() => {
                resetForm();
                setDialogOpen(true);
              }}
              className="bg-white text-indigo-600 hover:bg-indigo-50"
              data-testid="btn-nuovo-accordo"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Accordo Quadro
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <p className="text-indigo-100 text-sm">Totale Accordi</p>
              <p className="text-xl sm:text-2xl font-bold">{accordi.length}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <p className="text-indigo-100 text-sm">Attivi</p>
              <p className="text-xl sm:text-2xl font-bold">{accordi.filter(a => a.stato === 'attivo').length}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <p className="text-indigo-100 text-sm">Castelletto Totale (€)</p>
              <p className="text-xl sm:text-2xl font-bold">{formatCurrency(accordi.filter(a => a.tipo_conteggio !== 'ore').reduce((sum, a) => sum + (a.castelletto || 0), 0))}</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
              <p className="text-indigo-100 text-sm">Disponibile (€)</p>
              <p className="text-xl sm:text-2xl font-bold">{formatCurrency(accordi.filter(a => a.tipo_conteggio !== 'ore').reduce((sum, a) => sum + (a.castelletto_disponibile || 0), 0))}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 py-4 sm:px-6 sm:py-8">
        {/* Search */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
            <Input
              placeholder="Cerca per numero, nome o cliente..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              className="pl-10"
              data-testid="search-accordi"
            />
          </div>
        </div>

        {/* Lista Accordi */}
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-indigo-600" />
            <p className="text-slate-600 mt-2">Caricamento...</p>
          </div>
        ) : filteredAccordi.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
            <FileSpreadsheet className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Nessun Accordo Quadro</h3>
            <p className="text-slate-600 mb-6">Inizia creando il tuo primo accordo quadro</p>
            <Button
              onClick={() => {
                resetForm();
                setDialogOpen(true);
              }}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Accordo Quadro
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredAccordi.map((accordo) => (
              <div 
                key={accordo.id} 
                data-testid={`accordo-card-${accordo.id}`}
                onClick={(e) => { if (!e.target.closest('button, a, input')) handleViewDetails(accordo); }}
                className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-md hover:border-indigo-300 transition-shadow cursor-pointer"
              >
                <div className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="bg-indigo-100 p-3 rounded-lg">
                        <FileSpreadsheet className="w-6 h-6 text-indigo-600" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-bold text-indigo-700">{accordo.numero_accordo}</span>
                          {accordo.codice_accordo_quadro && (
                            <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded font-mono">
                              {accordo.codice_accordo_quadro}
                            </span>
                          )}
                          {getStatoBadge(accordo.stato)}
                        </div>
                        <p className="text-lg font-semibold text-slate-900">{accordo.nome_accordo}</p>
                        <p className="text-sm text-slate-600 flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          {accordo.cliente_nome}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-xs text-slate-500">Castelletto {accordo.tipo_conteggio === 'ore' ? '(h)' : '(€)'}</p>
                        <p className="text-lg font-bold text-slate-900">{formatCastelletto(accordo.castelletto, accordo.tipo_conteggio)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-500">Utilizzato</p>
                        <p className="text-lg font-bold text-blue-600">{formatCastelletto(accordo.castelletto_utilizzato, accordo.tipo_conteggio)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-500">Disponibile</p>
                        <div className="flex items-center gap-2 justify-end">
                          <p className={`text-lg font-bold ${
                            accordo.alert_level === 'critical' ? 'text-red-600' :
                            accordo.alert_level === 'warning' ? 'text-amber-600' :
                            'text-green-600'
                          }`}>
                            {formatCastelletto(accordo.castelletto_disponibile, accordo.tipo_conteggio)}
                          </p>
                          {accordo.alert_budget && (
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                              accordo.alert_level === 'critical' 
                                ? 'bg-red-100 text-red-700 animate-pulse' 
                                : 'bg-amber-100 text-amber-700'
                            }`}>
                              {accordo.percentuale_disponibile?.toFixed(0)}%
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-500">Fatturato</p>
                        <p className="text-lg font-bold text-purple-600">{formatCurrency(accordo.importo_fatturato)}</p>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRicalcolaCastelletto(accordo)}
                          data-testid={`ricalcola-accordo-${accordo.id}`}
                          className="text-green-600 border-green-200 hover:bg-green-50"
                          title="Ricalcola castelletto dai progetti"
                        >
                          <RefreshCw className="w-4 h-4 mr-1" />
                          Ricalcola
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openListinoDialog(accordo)}
                          className="text-amber-600 border-amber-200 hover:bg-amber-50"
                          data-testid={`listino-accordo-${accordo.numero_accordo}`}
                        >
                          <FileSpreadsheet className="w-4 h-4 mr-1" />
                          Listino
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewDetails(accordo)}
                          className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                          data-testid={`view-accordo-${accordo.numero_accordo}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Dettagli
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(accordo)}
                          data-testid={`edit-accordo-${accordo.id}`}
                          className="hover:bg-indigo-50 hover:text-indigo-600"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(accordo.id)}
                          data-testid={`delete-accordo-${accordo.id}`}
                          className="hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Progress bar - Utilizzo Castelletto */}
                  {accordo.castelletto > 0 && (
                    <div className="mt-4">
                      <div className="flex justify-between text-xs text-slate-500 mb-1">
                        <span>Utilizzo Castelletto</span>
                        <span>{Math.round((accordo.castelletto_utilizzato / accordo.castelletto) * 100)}%</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all"
                          style={{ width: `${Math.min((accordo.castelletto_utilizzato / accordo.castelletto) * 100, 100)}%` }}
                        />
                      </div>
                    </div>
                  )}

                  <p className="mt-2 text-xs text-slate-500" data-testid={`accordo-completati-${accordo.id}`}>
                    Scalano solo i progetti completati: {accordo.progetti_completati || 0}
                    {accordo.progetti_completati_senza_importo > 0 && (
                      <span className="ml-2 text-amber-700 font-medium" data-testid={`accordo-senza-prezzo-${accordo.id}`}>
                        · {accordo.progetti_completati_senza_importo} senza importo né voce di listino (non scalano)
                      </span>
                    )}
                  </p>

                  {/* Date validità */}
                  {(accordo.data_inizio_validita || accordo.data_fine_validita) && (
                    <div className="mt-3 flex items-center gap-4 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Validità: {accordo.data_inizio_validita ? new Date(accordo.data_inizio_validita).toLocaleDateString('it-IT') : '-'} 
                        {' → '} 
                        {accordo.data_fine_validita ? new Date(accordo.data_fine_validita).toLocaleDateString('it-IT') : '-'}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Dialog Nuovo/Modifica Accordo */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingAccordo ? 'Modifica Accordo Quadro' : 'Nuovo Accordo Quadro'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Label htmlFor="cliente_id">Cliente *</Label>
                <Select
                  value={formData.cliente_id}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, cliente_id: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {clienti.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.nome_azienda}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="sm:col-span-2">
                <Label htmlFor="nome_accordo">Nome Accordo *</Label>
                <Input
                  id="nome_accordo"
                  value={formData.nome_accordo}
                  onChange={(e) => setFormData(prev => ({ ...prev, nome_accordo: e.target.value }))}
                  placeholder="Es: Accordo Manutenzione 2026"
                />
              </div>

              <div className="sm:col-span-2">
                <Label htmlFor="descrizione">Descrizione</Label>
                <Textarea
                  id="descrizione"
                  value={formData.descrizione}
                  onChange={(e) => setFormData(prev => ({ ...prev, descrizione: e.target.value }))}
                  placeholder="Descrizione dell'accordo quadro..."
                  rows={3}
                />
              </div>

              <div>
                <Label htmlFor="codice_accordo_quadro">Codice Accordo Quadro</Label>
                <Input
                  id="codice_accordo_quadro"
                  value={formData.codice_accordo_quadro}
                  onChange={(e) => setFormData(prev => ({ ...prev, codice_accordo_quadro: e.target.value }))}
                  placeholder="Codice identificativo"
                />
              </div>

              <div>
                <Label htmlFor="tipo_conteggio">Tipo Conteggio</Label>
                <Select
                  value={formData.tipo_conteggio}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, tipo_conteggio: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="euro">Euro (€) - Importi fissi</SelectItem>
                    <SelectItem value="ore">Ore (h) - Conteggio orario</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  {formData.tipo_conteggio === 'ore' 
                    ? 'Il castelletto sarà conteggiato in ore lavorate' 
                    : 'Il castelletto sarà in Euro (€)'}
                </p>
              </div>

              <div>
                <Label htmlFor="castelletto">Castelletto ({formData.tipo_conteggio === 'ore' ? 'h' : '€'})</Label>
                <Input
                  id="castelletto"
                  type="number"
                  value={formData.castelletto}
                  onChange={(e) => setFormData(prev => ({ ...prev, castelletto: e.target.value }))}
                  placeholder={formData.tipo_conteggio === 'ore' ? 'Ore totali accordo' : 'Ammontare totale accordo'}
                />
                <p className="text-xs text-slate-500 mt-1">
                  {formData.tipo_conteggio === 'ore' 
                    ? 'Ore totali disponibili che verranno scalate con i progetti' 
                    : 'Budget totale che verrà scalato con i progetti collegati'}
                </p>
              </div>

              <div>
                <Label htmlFor="pm_main_solution_id">PM Main Solution</Label>
                <Select
                  value={formData.pm_main_solution_id}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, pm_main_solution_id: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona PM" />
                  </SelectTrigger>
                  <SelectContent>
                    {utenti.map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.nome} {u.cognome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="referente_cliente">Referente Cliente</Label>
                <Input
                  id="referente_cliente"
                  value={formData.referente_cliente}
                  onChange={(e) => setFormData(prev => ({ ...prev, referente_cliente: e.target.value }))}
                  placeholder="Nome referente"
                />
              </div>

              <div>
                <Label htmlFor="pm_cliente">PM Cliente</Label>
                <Input
                  id="pm_cliente"
                  value={formData.pm_cliente}
                  onChange={(e) => setFormData(prev => ({ ...prev, pm_cliente: e.target.value }))}
                  placeholder="PM lato cliente"
                />
              </div>

              <div>
                <Label htmlFor="data_stipula">Data Stipula</Label>
                <Input
                  id="data_stipula"
                  type="date"
                  value={formData.data_stipula}
                  onChange={(e) => setFormData(prev => ({ ...prev, data_stipula: e.target.value }))}
                />
              </div>

              <div>
                <Label htmlFor="data_inizio_validita">Data Inizio Validità</Label>
                <Input
                  id="data_inizio_validita"
                  type="date"
                  value={formData.data_inizio_validita}
                  onChange={(e) => setFormData(prev => ({ ...prev, data_inizio_validita: e.target.value }))}
                />
              </div>

              <div>
                <Label htmlFor="data_fine_validita">Data Fine Validità</Label>
                <Input
                  id="data_fine_validita"
                  type="date"
                  value={formData.data_fine_validita}
                  onChange={(e) => setFormData(prev => ({ ...prev, data_fine_validita: e.target.value }))}
                />
              </div>

              <div>
                <Label htmlFor="stato">Stato</Label>
                <Select
                  value={formData.stato}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, stato: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="attivo">Attivo</SelectItem>
                    <SelectItem value="in_scadenza">In Scadenza</SelectItem>
                    <SelectItem value="scaduto">Scaduto</SelectItem>
                    <SelectItem value="chiuso">Chiuso</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-wrap justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Annulla
              </Button>
              <Button onClick={handleSubmit} className="bg-indigo-600 hover:bg-indigo-700">
                {editingAccordo ? 'Aggiorna' : 'Crea Accordo'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Dettagli Accordo */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedAccordo && (
            <>
              <DialogHeader className="flex flex-row items-start justify-between">
                <DialogTitle className="text-2xl flex items-center gap-3">
                  <span className="font-mono text-indigo-600">{selectedAccordo.numero_accordo}</span>
                  <span>{selectedAccordo.nome_accordo}</span>
                  {getStatoBadge(selectedAccordo.stato)}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6 mt-4">
                {/* Info generali */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-slate-50 rounded-lg p-4">
                  <div>
                    <p className="text-sm text-slate-600">Cliente</p>
                    <p className="font-semibold">{selectedAccordo.cliente_nome}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Codice Accordo</p>
                    <p className="font-semibold font-mono">{selectedAccordo.codice_accordo_quadro || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">PM Main Solution</p>
                    <p className="font-semibold">{selectedAccordo.pm_main_solution_nome || '-'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Fatturato</p>
                    <p className="font-semibold text-purple-600">{formatCurrency(selectedAccordo.importo_fatturato)}</p>
                  </div>
                </div>

                {/* Castelletto */}
                <div className={`border rounded-lg p-4 ${
                  selectedAccordo.alert_level === 'critical' 
                    ? 'bg-red-50 border-red-300' 
                    : selectedAccordo.alert_level === 'warning'
                    ? 'bg-amber-50 border-amber-300'
                    : 'bg-indigo-50 border-indigo-200'
                }`}>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className={`text-lg font-bold ${
                      selectedAccordo.alert_level === 'critical' ? 'text-red-900' :
                      selectedAccordo.alert_level === 'warning' ? 'text-amber-900' :
                      'text-indigo-900'
                    }`}>
                      {selectedAccordo.tipo_conteggio === 'ore' ? '⏱️ Castelletto (Ore)' : '💰 Castelletto (Euro)'}
                    </h3>
                    {selectedAccordo.alert_budget && (
                      <span className={`px-3 py-1 text-sm font-bold rounded-full ${
                        selectedAccordo.alert_level === 'critical' 
                          ? 'bg-red-600 text-white animate-pulse' 
                          : 'bg-amber-500 text-white'
                      }`}>
                        ⚠️ {selectedAccordo.percentuale_disponibile?.toFixed(0)}% disponibile
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-xs text-slate-600">Totale</p>
                      <p className="text-xl font-bold text-slate-900">{formatCastelletto(selectedAccordo.castelletto, selectedAccordo.tipo_conteggio)}</p>
                    </div>
                    <div className="bg-white rounded-lg p-3">
                      <p className="text-xs text-slate-600">Utilizzato</p>
                      <p className="text-xl font-bold text-blue-600">{formatCastelletto(selectedAccordo.castelletto_utilizzato, selectedAccordo.tipo_conteggio)}</p>
                    </div>
                    <div className={`rounded-lg p-3 ${
                      selectedAccordo.alert_level === 'critical' ? 'bg-red-100' :
                      selectedAccordo.alert_level === 'warning' ? 'bg-amber-100' :
                      'bg-white'
                    }`}>
                      <p className="text-xs text-slate-600">Disponibile</p>
                      <p className={`text-xl font-bold ${
                        selectedAccordo.alert_level === 'critical' ? 'text-red-600' :
                        selectedAccordo.alert_level === 'warning' ? 'text-amber-600' :
                        'text-green-600'
                      }`}>{formatCastelletto(selectedAccordo.castelletto_disponibile, selectedAccordo.tipo_conteggio)}</p>
                    </div>
                  </div>
                  {selectedAccordo.castelletto > 0 && (
                    <div className="mt-3">
                      <div className="flex justify-between text-xs text-indigo-700 mb-1">
                        <span>Utilizzo</span>
                        <span>{Math.round((selectedAccordo.castelletto_utilizzato / selectedAccordo.castelletto) * 100)}%</span>
                      </div>
                      <div className="h-3 bg-indigo-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-indigo-600 rounded-full transition-all"
                          style={{ width: `${Math.min((selectedAccordo.castelletto_utilizzato / selectedAccordo.castelletto) * 100, 100)}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Progetti Collegati - Solo visualizzazione */}
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-bold text-purple-900">📁 Progetti Collegati</h3>
                    <div className="flex gap-2">
                      {progettiCollegati.length > 0 && (
                        <Button
                          onClick={() => handleExportProgettiExcel()}
                          variant="outline"
                          size="sm"
                          className="border-green-300 text-green-700 hover:bg-green-50"
                        >
                          <FileSpreadsheet className="w-4 h-4 mr-1" />
                          Export Excel
                        </Button>
                      )}
                      <Button
                        onClick={() => navigate('/progetti')}
                        variant="outline"
                        size="sm"
                        className="text-purple-600 border-purple-300 hover:bg-purple-100"
                      >
                        Vai a Progetti
                      </Button>
                    </div>
                  </div>

                  {loadingProgetti ? (
                    <div className="text-center py-4">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto text-purple-600" />
                    </div>
                  ) : progettiCollegati.length > 0 ? (
                    <div className="space-y-6">
                      {getProgettiPerMese().map(([mese, progetti]) => {
                        // Determina lo stato fatturazione prevalente del mese
                        const statoMese = progetti[0]?.stato_fatturazione || 'non_fatturabile';
                        const codiceOdaMese = progetti[0]?.codice_oda || '';
                        
                        return (
                        <div key={mese} className="border border-purple-200 rounded-lg overflow-hidden">
                          {/* Header mese con dropdown fatturazione */}
                          <div className="bg-purple-100 px-4 py-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-purple-600" />
                                <span className="font-semibold text-purple-900">
                                  {mese === 'Non assegnato' ? mese : new Date(mese + '-01').toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}
                                </span>
                                <span className="text-xs text-purple-600">({progetti.length} progetti)</span>
                              </div>
                            </div>
                            
                            {/* Controlli Fatturazione per tutto il mese */}
                            <div className="flex items-center justify-end gap-3 mt-2 pt-2 border-t border-purple-200">
                              {/* Codice OdA per il mese */}
                              <div className="flex items-center gap-1">
                                <span className="text-xs text-slate-600">OdA:</span>
                                <Input
                                  value={codiceOdaMese}
                                  onChange={(e) => handleUpdateMeseFatturazione(mese, progetti, 'codice_oda', e.target.value)}
                                  className="h-7 w-32 text-xs"
                                  placeholder="Codice OdA"
                                />
                              </div>
                              
                              {/* Stato Fatturazione per il mese */}
                              <Select
                                value={statoMese}
                                onValueChange={(value) => handleUpdateMeseFatturazione(mese, progetti, 'stato_fatturazione', value)}
                              >
                                <SelectTrigger className="h-7 w-[150px] text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="non_fatturabile">
                                    <span className="flex items-center gap-1">
                                      <span className="w-2 h-2 rounded-full bg-slate-400"></span>
                                      Non Fatturabile
                                    </span>
                                  </SelectItem>
                                  <SelectItem value="da_fatturare">
                                    <span className="flex items-center gap-1">
                                      <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                                      Da Fatturare
                                    </span>
                                  </SelectItem>
                                  <SelectItem value="fatturato">
                                    <span className="flex items-center gap-1">
                                      <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                      Fatturato
                                    </span>
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          
                          {/* Lista progetti del mese */}
                          <div className="divide-y divide-purple-100">
                            {progetti.map((progetto) => (
                              <div 
                                key={progetto.id} 
                                className="bg-white p-3 hover:bg-purple-50 transition-colors"
                              >
                                <div 
                                  className="flex items-start justify-between gap-4 cursor-pointer"
                                  onClick={() => navigate(`/progetti?highlight=${progetto.id}`)}
                                >
                                  {/* Info progetto */}
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="font-mono text-sm text-purple-600">{progetto.numero_progetto}</span>
                                      {(progetto.voce_listino_nome || progetto.attivita_accordo) && (
                                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                                          {progetto.voce_listino_nome || `Attività ${progetto.attivita_accordo}`}
                                        </span>
                                      )}
                                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                        progetto.stato === 'completato' ? 'bg-green-100 text-green-700' :
                                        progetto.stato === 'in_corso' ? 'bg-blue-100 text-blue-700' :
                                        progetto.stato === 'in_validazione' ? 'bg-yellow-100 text-yellow-700' :
                                        'bg-slate-100 text-slate-700'
                                      }`}>
                                        {progetto.stato?.replace('_', ' ') || 'Non iniziato'}
                                      </span>
                                    </div>
                                    <p className="font-medium text-slate-900">{progetto.nome_progetto}</p>
                                    <div className="flex items-center gap-4 text-xs text-slate-500 mt-1">
                                      <span>{progetto.ingegnere_responsabile_nome}</span>
                                      {(progetto.data_inizio || progetto.data_fine_prevista) && (
                                        <span className="flex items-center gap-1">
                                          <Calendar className="w-3 h-3" />
                                          {progetto.data_inizio || '?'} → {progetto.data_fine_prevista || progetto.data_fine_effettiva || '?'}
                                        </span>
                                      )}
                                    </div>
                                    {progetto.varianti && (
                                      <p className="text-xs text-amber-600 mt-1">Varianti: {progetto.varianti}</p>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Storico Modifiche Date (se presente) */}
                                {progetto.storico_modifiche_date && progetto.storico_modifiche_date.length > 0 && (
                                  <div className="mt-2 ml-4 border-l-2 border-amber-300 pl-3">
                                    <div className="flex items-center gap-1 text-xs text-amber-700 font-medium mb-1">
                                      <History className="w-3 h-3" />
                                      Modifiche Date ({progetto.storico_modifiche_date.length})
                                    </div>
                                    <div className="space-y-1">
                                      {progetto.storico_modifiche_date.map((mod, idx) => (
                                        <div key={idx} className="text-xs bg-amber-50 rounded px-2 py-1">
                                          <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-slate-500">
                                              {new Date(mod.data_modifica).toLocaleDateString('it-IT')}
                                            </span>
                                            <span className="font-medium text-amber-800">
                                              {mod.campo === 'data_fine_prevista' ? 'Fine Prevista' : 'Inizio'}:
                                            </span>
                                            <span className="text-red-500 line-through">{mod.valore_precedente}</span>
                                            <span>→</span>
                                            <span className="text-green-600 font-medium">{mod.nuovo_valore}</span>
                                          </div>
                                          <div className="text-slate-600 mt-0.5">
                                            <span className="italic">&quot;{mod.giustificazione}&quot;</span>
                                            <span className="text-slate-400 ml-2">- {mod.utente}</span>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                        );
                      })}
                      
                      {/* Totale generale */}
                      <div className="mt-3 pt-3 border-t border-purple-200 flex justify-between text-sm">
                        <span className="text-purple-700 font-medium">{progettiCollegati.length} progetti · scalati dai {selectedAccordo.progetti_completati || 0} completati</span>
                        <span className="font-bold text-purple-900">
                          {formatCastelletto(selectedAccordo.castelletto_utilizzato, selectedAccordo.tipo_conteggio)}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-6 bg-white rounded-lg border-2 border-dashed border-purple-200">
                      <FileText className="w-10 h-10 text-purple-300 mx-auto mb-2" />
                      <p className="text-slate-500">Nessun progetto collegato</p>
                      <p className="text-xs text-slate-400 mt-1">Crea un progetto dalla sezione Progetti e collegalo a questo accordo</p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Gestione Listino Attività */}
      <Dialog open={listinoDialogOpen} onOpenChange={setListinoDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <FileSpreadsheet className="w-6 h-6" />
              Listino Attività - {selectedAccordo?.codice_accordo_quadro || selectedAccordo?.numero_accordo}
            </DialogTitle>
          </DialogHeader>

          {selectedAccordo && (
            <div className="space-y-6">
              {/* Info AQ */}
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-amber-900">{selectedAccordo.nome_accordo}</p>
                    <p className="text-sm text-amber-700">
                      Tipo conteggio: <strong>{selectedAccordo.tipo_conteggio === 'ore' ? 'A Ore' : 'In Euro (€)'}</strong>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-amber-600">Attività nel listino</p>
                    <p className="text-2xl font-bold text-amber-800">{listino.length}</p>
                  </div>
                </div>
              </div>

              {/* Azioni Import/Export */}
              <div className="flex gap-3 flex-wrap">
                <Button
                  variant="outline"
                  onClick={() => handleDownloadTemplate(selectedAccordo.id)}
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Scarica Template Excel
                </Button>
                <div>
                  <input
                    type="file"
                    ref={listinoFileRef}
                    onChange={handleImportListino}
                    accept=".xlsx,.xls"
                    className="hidden"
                  />
                  <Button
                    variant="outline"
                    onClick={() => listinoFileRef.current?.click()}
                    className="border-green-300 text-green-700 hover:bg-green-50"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Importa da Excel
                  </Button>
                </div>
              </div>

              {/* Aggiungi voce manualmente */}
              <div className="bg-slate-50 rounded-lg p-4 border">
                <Label className="text-sm font-medium text-slate-700 mb-2 block">Aggiungi voce manualmente</Label>
                <div className="grid grid-cols-12 gap-3 items-end">
                  <div className="col-span-5">
                    <Label className="text-xs text-slate-500">Descrizione *</Label>
                    <Input
                      placeholder="Descrizione attività..."
                      value={nuovaVoceListino.descrizione}
                      onChange={(e) => setNuovaVoceListino(prev => ({ ...prev, descrizione: e.target.value }))}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-slate-500">Unità Misura</Label>
                    <Select
                      value={nuovaVoceListino.unita_misura}
                      onValueChange={(value) => setNuovaVoceListino(prev => ({ ...prev, unita_misura: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="euro">€ (importo fisso)</SelectItem>
                        <SelectItem value="euro_ora">€/h (tariffa oraria)</SelectItem>
                        <SelectItem value="ore">h (solo ore)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-slate-500">
                      {nuovaVoceListino.unita_misura === 'ore' ? 'Ore' : (nuovaVoceListino.unita_misura === 'euro_ora' ? 'Tariffa €/h' : 'Prezzo €')}
                    </Label>
                    <Input
                      type="number"
                      placeholder={nuovaVoceListino.unita_misura === 'ore' ? 'Ore' : '€'}
                      value={nuovaVoceListino.prezzo}
                      onChange={(e) => setNuovaVoceListino(prev => ({ ...prev, prezzo: e.target.value }))}
                    />
                  </div>
                  {nuovaVoceListino.unita_misura === 'euro_ora' && (
                    <div className="col-span-1">
                      <Label className="text-xs text-slate-500">Ore</Label>
                      <Input
                        type="number"
                        placeholder="h"
                        value={nuovaVoceListino.ore}
                        onChange={(e) => setNuovaVoceListino(prev => ({ ...prev, ore: e.target.value }))}
                      />
                    </div>
                  )}
                  <div className={nuovaVoceListino.unita_misura === 'euro_ora' ? 'col-span-2' : 'col-span-3'}>
                    <Button onClick={handleAddVoceListino} className="bg-amber-600 hover:bg-amber-700 w-full">
                      <Plus className="w-4 h-4 mr-1" />
                      Aggiungi
                    </Button>
                  </div>
                </div>
              </div>

              {/* Lista attività */}
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">
                  Attività nel listino ({listino.length})
                </Label>
                
                {loadingListino ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-amber-600" />
                  </div>
                ) : listino.length === 0 ? (
                  <div className="text-center py-8 bg-slate-50 rounded-lg border-2 border-dashed">
                    <FileSpreadsheet className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-500">Nessuna attività nel listino</p>
                    <p className="text-xs text-slate-400 mt-1">Scarica il template, compilalo e importalo</p>
                  </div>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="w-[50%]">Descrizione</TableHead>
                          <TableHead className="text-center">Unità</TableHead>
                          <TableHead className="text-right">Valore</TableHead>
                          <TableHead className="text-right">Ore</TableHead>
                          <TableHead className="w-16"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {listino.map((voce) => (
                          <TableRow key={voce.id} className="hover:bg-amber-50">
                            <TableCell className="font-medium">{voce.descrizione}</TableCell>
                            <TableCell className="text-center">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                voce.unita_misura === 'ore' ? 'bg-blue-100 text-blue-700' :
                                voce.unita_misura === 'euro_ora' ? 'bg-purple-100 text-purple-700' :
                                'bg-green-100 text-green-700'
                              }`}>
                                {voce.unita_misura === 'ore' ? 'h' : voce.unita_misura === 'euro_ora' ? '€/h' : '€'}
                              </span>
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {voce.unita_misura === 'ore' 
                                ? '-' 
                                : voce.prezzo !== undefined 
                                  ? `€ ${voce.prezzo.toLocaleString('it-IT')}` 
                                  : '-'}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {voce.ore ? `${voce.ore}h` : '-'}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteVoceListino(voce.id)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

